import React, { useCallback, useEffect } from "react";
import { Drawer } from "rsuite";
import PropTypes from "prop-types";
import { useSearchParams, useNavigate } from "react-router-dom";
import "./promotions.scss";
import CreatePromo from "../../components/create-promo/CreatePromo";
import { deepClone } from "../../constants/utils";
import {
  PROMO_OBJECT,
  DEFAULT_CALCULATION_TYPE,
  DEFAULT_CALCULATION_TYPE_FOR_PEGS_OFFLINE,
  PEGS_CRS_ID,
} from "./constants";
import { OFFLINE_CRS_ID } from "../../constants";
import { PROMO_QUERY_PARAM } from "../../constants/promoExpiry";
import starsBlue from "../../assets/img/stars_blue.svg";

const PromotionCreatePromo = ({
  ratePlans,
  rooms,
  ratePlansPropertyId = null,
  modal,
  setModal,
  promo,
  setPromo,
  isEdit,
  isMarket,
  setIsMarket,
  setIsEdit,
  setIsGetPromos,
  setLoading,
  calculationType,
  setCalculationType,
  property,
  isDuplicate = false,
  setIsDuplicate,
  isViewOnly = false,
  setIsViewOnly,
  isPilotHotel = false,
  onRecommendationUsed = null,
}) => {
  // Determine modal title based on mode
  const getModalTitle = () => {
    if (isViewOnly) {
      return "View Promo";
    }
    if (isDuplicate) {
      return "Duplicate Promo";
    }
    // If creating from recommendation, always show "Create a Promo"
    if (isMarket) {
      return "Create a Promo";
    }
    // Check if editing an existing promo (has promo ID)
    // Note: We don't check isEdit because it's set to false initially and only
    // becomes true after the API call completes. Having a promo ID is sufficient.
    const hasPromoId = promo?.id || promo?._derivedRateMeta?.promoId;
    if (hasPromoId) {
      return "Edit Promo";
    }
    return "Create a Promo";
  };

  // Recompute title whenever dependencies change
  const modalTitle = getModalTitle();

  const [params] = useSearchParams();
  const navigate = useNavigate();

  // Drop the params that opened this drawer so closing it doesn't reopen on the
  // next render, and a refresh lands on the plain list rather than re-counting
  // the CTA. `src` goes with promoId — left behind, it would re-attribute a
  // later manual open to the card or the email. Any other param is preserved.
  const clearPromoUrlParams = useCallback(() => {
    const currentParams = new URLSearchParams(globalThis.location.search);
    const drawerParams = [
      "pcode",
      "promoid",
      PROMO_QUERY_PARAM.promoId,
      PROMO_QUERY_PARAM.source,
    ];
    if (!drawerParams.some((param) => currentParams.has(param))) return;

    drawerParams.forEach((param) => currentParams.delete(param));
    const query = currentParams.toString();
    const search = query ? `?${query}` : "";
    navigate(`${globalThis.location.pathname}${search}`, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (params.get("createPromo") === "true") {
      toggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreatePromoFromScratch = () => {
    setModal(false);
    setPromo(null);
    navigate("/promotions?createPromo=true");
  };

  const toggle = () => {
    if (modal) {
      // Closing modal - clear search params if they exist
      setPromo(null);
      setIsDuplicate(false);
      clearPromoUrlParams();
    } else {
      // Opening modal
      setPromo(deepClone(PROMO_OBJECT));
      const { crsId } = property;
      if (PEGS_CRS_ID === crsId || OFFLINE_CRS_ID === crsId) {
        setCalculationType(DEFAULT_CALCULATION_TYPE_FOR_PEGS_OFFLINE);
      } else {
        setCalculationType(DEFAULT_CALCULATION_TYPE);
      }
    }
    setModal(!modal);
    setIsEdit(false);
    setIsMarket(false);
    if (setIsViewOnly) {
      setIsViewOnly(false);
    }
  };

  const promoSaved = () => {
    setModal(false);
    setPromo(null);
    setIsGetPromos({});
    setIsDuplicate(false);
    clearPromoUrlParams();
  };

  // No longer needed - title is always "Create a Promo"
  const stepperChangeCallback = useCallback(() => {}, []);

  return (
    <Drawer
      placement="right"
      open={modal}
      onClose={toggle}
      size="lg"
      className="create-promo-drawer"
    >
      <Drawer.Header>
        <Drawer.Title>{modalTitle}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        {isMarket && (
          <div className="review-smart-promo-banner">
            <div className="review-smart-promo-content">
              <div className="review-smart-promo-left">
                <img
                  src={starsBlue}
                  alt=""
                  width="16"
                  height="16"
                  className="sparkle-icon"
                />
                <span className="review-smart-promo-text">
                  Review your promo recommendation
                </span>
              </div>
              <button
                type="button"
                className="create-from-scratch-link"
                onClick={handleCreatePromoFromScratch}
              >
                Create Promo from scratch
              </button>
            </div>
          </div>
        )}

        <CreatePromo
          promo={promo}
          setPromo={setPromo}
          stepperChangeCallback={stepperChangeCallback}
          ratePlans={ratePlans}
          rooms={rooms}
          ratePlansPropertyId={ratePlansPropertyId}
          isEdit={isEdit}
          isMarket={isMarket}
          isDuplicate={isDuplicate}
          isViewOnly={isViewOnly}
          hideModal={promoSaved}
          setLoading={setLoading}
          calculationType={calculationType}
          setCalculationType={setCalculationType}
          isPilotHotel={isPilotHotel}
          onRecommendationUsed={onRecommendationUsed}
        />
      </Drawer.Body>
    </Drawer>
  );
};

PromotionCreatePromo.propTypes = {
  ratePlans: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string,
    })
  ).isRequired,
  rooms: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string,
    })
  ).isRequired,
  ratePlansPropertyId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  modal: PropTypes.bool.isRequired,
  setModal: PropTypes.func.isRequired,
  promo: PropTypes.shape({
    rateRule: PropTypes.shape({
      ruleVO: PropTypes.object,
    }),
  }),
  setPromo: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  setIsEdit: PropTypes.func.isRequired,
  setIsGetPromos: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  calculationType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setCalculationType: PropTypes.func.isRequired,
  property: PropTypes.shape({
    propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    propertyCode: PropTypes.string.isRequired,
    crsId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  isDuplicate: PropTypes.bool,
  setIsDuplicate: PropTypes.func.isRequired,
  isViewOnly: PropTypes.bool,
  setIsViewOnly: PropTypes.func,
  isPilotHotel: PropTypes.bool,
  onRecommendationUsed: PropTypes.func,
};

export default PromotionCreatePromo;
